import React, { useMemo } from 'react'
import { Button, Spin, Typography } from '@surecompanies/core_components'
import { ArrowLeftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { usePaymentBotCarrierDetailQuery } from '../../../../api/payment-bot-dashboard-api-client'
import type { CarrierRow, CarrierIdSelection } from '../../shared/types'
import { ALL_CARRIERS } from '../../shared/constants'
import { CarrierDetailHeader } from './CarrierDetailHeader'
import { CarrierDetailKpiRow } from './CarrierDetailKpiRow'
import { CarrierMonthTrendChart } from './CarrierMonthTrendChart'
import { ErrorBreakdownChart } from './ErrorBreakdownChart'
import { CarrierRecentFailuresTable } from './CarrierRecentFailuresTable'
import { useCanViewMemberFailures } from './hooks/useCanViewMemberFailures'
import { useCarrierFailures } from './hooks/useCarrierFailures'
import styles from './CarrierDetail.module.scss'

const { Text, Title } = Typography

interface Props {
  carrierId: CarrierIdSelection
  month: number
  year: number
  availableCarrierRows: CarrierRow[]
  isCarrierListLoading?: boolean
  onBack: () => void
}

export function CarrierDetail({
  carrierId,
  month,
  year,
  availableCarrierRows,
  isCarrierListLoading = false,
  onBack,
}: Props) {
  const resolvedCarrierName = useMemo(
    () =>
      carrierId !== ALL_CARRIERS
        ? (availableCarrierRows.find((r) => r.carrierId === carrierId)
            ?.carrierName ?? null)
        : null,
    [carrierId, availableCarrierRows]
  )

  const { data, isLoading, isError, error } = usePaymentBotCarrierDetailQuery(
    {
      carrierName: resolvedCarrierName ?? undefined,
      month,
      year,
    },
    { enabled: carrierId === ALL_CARRIERS || resolvedCarrierName !== null }
  )

  const failures = useCarrierFailures({
    carrierId,
    month,
    year,
    resolvedCarrierName,
  })

  const canViewMemberFailures = useCanViewMemberFailures(resolvedCarrierName)

  const carrierNamePending =
    carrierId !== ALL_CARRIERS && resolvedCarrierName === null
  const isCarrierUnresolved = carrierNamePending && !isCarrierListLoading

  const showLoading = isLoading || (carrierNamePending && isCarrierListLoading)

  return (
    <div data-testid="carrier-detail">
      {isCarrierUnresolved ? (
        <div
          className={styles.loadingWrap}
          data-testid="carrier-detail-not-found"
        >
          <div className={styles.stateMessage}>
            <Text type="secondary">
              Carrier not found in the current period.
            </Text>
            <div className={styles.stateBack}>
              <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                Back
              </Button>
            </div>
          </div>
        </div>
      ) : showLoading ? (
        <div className={styles.loadingWrap}>
          <Spin size="large" />
        </div>
      ) : isError ? (
        <div className={styles.loadingWrap} data-testid="carrier-detail-error">
          <div className={styles.stateMessage}>
            <Text type="danger">
              Failed to load carrier detail
              {error?.message ? ` — ${error.message}` : ''}
            </Text>
            <div className={styles.stateBack}>
              <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                Back
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <CarrierDetailHeader
            displayCarrierName={data?.carrierName}
            botType={data?.botType}
            exportCarrierName={resolvedCarrierName ?? undefined}
            showAttemptsExport={canViewMemberFailures}
            month={month}
            year={year}
            onBack={onBack}
          />

          {data &&
            (data.totalBenefits === 0 ? (
              <div
                className={styles.emptyState}
                data-testid="carrier-detail-empty"
              >
                <Title level={5} type="secondary" style={{ marginBottom: 8 }}>
                  No bot activity for{' '}
                  {dayjs().year(year).month(month).format('MMMM YYYY')}
                </Title>
                <Text type="secondary">
                  {carrierId === ALL_CARRIERS
                    ? 'No carriers ran payments in the selected billing period — pick an earlier month to see metrics.'
                    : 'This carrier ran no payments in the selected billing period — pick an earlier month to see metrics.'}
                </Text>
              </div>
            ) : (
              <>
                <CarrierDetailKpiRow data={data} />
                <CarrierMonthTrendChart
                  monthHistory={data.monthHistory}
                  carrierName={data.carrierName}
                />
                <ErrorBreakdownChart
                  errorBreakdown={data.errorBreakdown}
                  onToggleErrorCode={failures.toggleErrorCode}
                />
                {canViewMemberFailures && (
                  <CarrierRecentFailuresTable
                    failures={failures}
                    availableCarrierRows={availableCarrierRows}
                  />
                )}
              </>
            ))}
        </>
      )}
    </div>
  )
}
